/**
 * Infinite scroll enhancement for scroll-carousel elements
 * Wraps carousel navigation to create seamless infinite scrolling
 */
(function() {
  'use strict';

  // Wait for DOM and custom elements to be ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  function init() {
    // Wait a bit to ensure scroll-carousel is defined
    setTimeout(() => {
      const carousels = document.querySelectorAll('scroll-carousel[infinite-scroll]');
      carousels.forEach(carousel => enableInfiniteScroll(carousel));
    }, 100);
  }

  function enableInfiniteScroll(carousel) {
    // GRIT-6631: this file is deferred and can run before theme.js (a module) defines
    // scroll-carousel; wait for the definition instead of failing and leaving the flag set.
    if (!window.customElements.get('scroll-carousel')) {
      window.customElements.whenDefined('scroll-carousel').then(function () {
        enableInfiniteScroll(carousel);
      });
      return;
    }

    // Prevent double initialization
    if (carousel.dataset.infiniteScrollInitialized) return;
    carousel.dataset.infiniteScrollInitialized = 'true';

    let isTransitioning = false;
    const isMobile = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

    // Store original methods
    const originalNext = carousel.next.bind(carousel);
    const originalPrevious = carousel.previous.bind(carousel);

    // Create and add pagination dots (desktop only)
    if (!isMobile) {
      const realCells = Array.from(carousel.children).filter(c => !c.classList.contains('infinite-clone'));
      const totalCells = realCells.length;

      carousel.parentElement.style.position = 'relative';
    }

    // Override next method for infinite scrolling
    carousel.next = function(options = {}) {
      if (isTransitioning) return;

      const currentIndex = this.selectedIndex;
      const realCells = Array.from(this.children).filter(c => !c.classList.contains('infinite-clone'));
      const totalCells = realCells.length;

      if (currentIndex >= totalCells - this.groupCells) {
        // At the last real item, clone first item and slide to it
        isTransitioning = true;

        // Clone the first real item
        const firstClone = realCells[0].cloneNode(true);
        firstClone.classList.add('infinite-clone');
        this.appendChild(firstClone);

        // Slide to the clone smoothly
        setTimeout(() => {
          this.select(totalCells, { instant: false });
        }, 10);

        // After slide completes, remove clone and jump to real first
        setTimeout(() => {
          firstClone.remove();
          this.select(0, { instant: true });
          isTransitioning = false;
        }, 600);
      } else {
        // Use original next behavior
        originalNext(options);
      }
    };

    // Override previous method for infinite scrolling
    carousel.previous = function(options = {}) {
      if (isTransitioning) return;

      const currentIndex = this.selectedIndex;
      const realCells = Array.from(this.children).filter(c => !c.classList.contains('infinite-clone'));
      const totalCells = realCells.length;

      if (currentIndex === 0) {
        // At the first real item, clone last item and slide to it
        isTransitioning = true;

        // Clone the last real item
        const lastClone = realCells[realCells.length - 1].cloneNode(true);
        lastClone.classList.add('infinite-clone');
        this.insertBefore(lastClone, this.firstChild);

        // Slide to the clone smoothly (now at index 0)
        setTimeout(() => {
          this.select(0, { instant: false });
        }, 10);

        // After slide completes, remove clone and jump to real last
        setTimeout(() => {
          lastClone.remove();
          this.select(totalCells - this.groupCells, { instant: true });
          isTransitioning = false;
        }, 600);
      } else {
        // Use original previous behavior
        originalPrevious(options);
      }
    };

    // Prevent edge events from disabling navigation buttons
    carousel.addEventListener('scroll:edge-nearing', (event) => {
      event.stopImmediatePropagation();
    }, true);

    carousel.addEventListener('scroll:edge-leaving', (event) => {
      event.stopImmediatePropagation();
    }, true);

    // Keep navigation buttons always enabled
    const carouselId = carousel.getAttribute('id');
    if (carouselId) {
      const prevButton = document.querySelector(`button[is="carousel-prev-button"][aria-controls="${carouselId}"]`);
      const nextButton = document.querySelector(`button[is="carousel-next-button"][aria-controls="${carouselId}"]`);

      if (prevButton) {
        prevButton.disabled = false;
        // Override button disabled setter
        Object.defineProperty(prevButton, 'disabled', {
          get: function() { return false; },
          set: function() { /* ignore */ }
        });
      }

      if (nextButton) {
        nextButton.disabled = false;
        // Override button disabled setter
        Object.defineProperty(nextButton, 'disabled', {
          get: function() { return false; },
          set: function() { /* ignore */ }
        });
      }
    }

    // Handle scroll events for automatic wrapping
    let isWrapping = false;

    carousel.addEventListener('carousel:change', (event) => {
      if (isWrapping) {
        isWrapping = false;
        return;
      }

      const currentIndex = carousel.selectedIndex;
      const totalCells = carousel.cells.length;

      // Optional: Add visual indicators or other effects when wrapping
      if (currentIndex === 0 || currentIndex === totalCells - 1) {
        carousel.classList.add('at-boundary');
      } else {
        carousel.classList.remove('at-boundary');
      }
    });

    // Mobile-specific: Handle touch-based scrolling for infinite wrap
    if (isMobile) {
      let touchStartX = 0;
      let touchEndX = 0;
      let touchStartIndex = 0;

      // Track when user starts swiping
      carousel.addEventListener('touchstart', (e) => {
        touchStartX = e.touches[0].clientX;
        touchStartIndex = carousel.selectedIndex;
      }, { passive: true });

      // Track swipe direction and handle wrapping
      carousel.addEventListener('touchend', (e) => {
        if (isTransitioning) return;

        touchEndX = e.changedTouches[0].clientX;
        const swipeDistance = touchStartX - touchEndX;
        const minSwipeDistance = 50; // Minimum swipe distance to trigger

        if (Math.abs(swipeDistance) < minSwipeDistance) return;

        const realCells = Array.from(carousel.children).filter(c => !c.classList.contains('infinite-clone'));
        const totalCells = realCells.length;
        const touchEndIndex = carousel.selectedIndex;

        // Only wrap if user started the swipe from the last/first item AND tried to go further
        // Swiped left (next) from last item
        if (swipeDistance > 0 && touchStartIndex >= totalCells - carousel.groupCells && touchEndIndex >= totalCells - carousel.groupCells) {
          isTransitioning = true;
          setTimeout(() => {
            carousel.select(0, { instant: true });
            isTransitioning = false;
          }, 300);
        }
        // Swiped right (previous) from first item
        else if (swipeDistance < 0 && touchStartIndex === 0 && touchEndIndex === 0) {
          isTransitioning = true;
          setTimeout(() => {
            carousel.select(totalCells - carousel.groupCells, { instant: true });
            isTransitioning = false;
          }, 300);
        }
      }, { passive: true });
    }
  }

  // Also handle dynamically added carousels
  if ('MutationObserver' in window && document.body) {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) { // Element node
            if (node.matches && node.matches('scroll-carousel[infinite-scroll]')) {
              enableInfiniteScroll(node);
            }
            // Check children
            const carousels = node.querySelectorAll && node.querySelectorAll('scroll-carousel[infinite-scroll]');
            if (carousels) {
              carousels.forEach(enableInfiniteScroll);
            }
          }
        });
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  // Expose method globally for manual initialization
  window.initInfiniteScroll = function(carousel) {
    if (carousel && carousel.hasAttribute && carousel.hasAttribute('infinite-scroll')) {
      enableInfiniteScroll(carousel);
    }
  };
})();
